import ExpanderList, { ExpanderListItem } from '@rio-cloud/rio-uikit/ExpanderList';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import {
    compoundManagementSlice,
    getActiveCompoundManagementView,
    getSelectedCompoundVehicle,
} from '../../../reducers/compoundManagementSlice';
import { AllowedAction, CompoundManagementView, DepartedVehicle } from '../../compoundManagement.types';
import { ArrivalDetails, ArrivalDetailsProps } from '../../shared/sidebar/ArrivalDetails';
import { CompoundLocationAndDestinationDetails } from '../../shared/sidebar/CompoundLocationAndDestinationDetails';
import { CompoundVehicleVIN } from '../../shared/sidebar/CompoundVehicleVIN';
import { VehicleDetails } from '../../shared/sidebar/VehicleDetails';
import { DepartureDetails, DepartureDetailsProps } from './DepartureDetails';
import { DepartureDetailsHeader } from '../../shared/sidebar/DepartureDetailsHeader';
import { CancelledArrivalDetailsHeader } from '../../shared/sidebar/CancelledArrivalDetailsHeader';
import { ArrivalDetailsHeader, NoArrivalDetailsHeader } from '../../shared/sidebar/ArrivalDetailsHeader';
import { CancelledDepartureDetailsHeader } from '../../shared/sidebar/CancelledDepartureDetailsHeader';
import { CustomsInformationExpander } from '../../../sharedComponents/sidebar/CustomsInformationExpander';

export const DepartedVehiclesSidebar: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const selectedVehicle = useAppSelector(getSelectedCompoundVehicle) as DepartedVehicle;
    const activeManagementView = useAppSelector(getActiveCompoundManagementView);

    const sidebarProperties = {
        showHeaderBorder: true,
        resizable: true,
        position: Sidebar.RIGHT,
        minWidth: 400,
        maxWidth: 550,
        width: 550,
        fly: true,
    };

    const closeSidebar = () => {
        dispatch(compoundManagementSlice.actions.deselectCompoundVehicle());
    };

    if (selectedVehicle !== undefined && activeManagementView === CompoundManagementView.DEPARTED) {
        const title = <FormattedMessage id="outboundOrderBook.compoundManagement.common.sidebar.header.titleVehicle" />;

        const footerButton = (
            <div className="display-flex gap-10 flex-row-reverse">
                <button className="btn btn-default" type="button" onClick={closeSidebar}>
                    <FormattedMessage id="outboundOrderBook.common.button.close" />
                </button>
            </div>
        );

        const arrivalDetails = selectedVehicle.arrival
            ? buildArrivalDetailsItem({
                  compoundVehicleId: selectedVehicle.id,
                  compoundLocation: selectedVehicle.compoundLocation,
                  arrival: selectedVehicle.arrival,
                  disabled: !selectedVehicle.allowedActions.includes(AllowedAction.REPORT_ARRIVAL),
              })
            : buildNoArrivalDetailsItem();

        const departureDetails = buildDepartureDetailsItem({
            compoundVehicleId: selectedVehicle.id,
            compoundLocation: selectedVehicle.compoundLocation,
            departure: selectedVehicle.departure,
            disabled:
                !selectedVehicle.allowedActions.includes(AllowedAction.REPORT_ANNOUNCED_DEPARTURE) &&
                !selectedVehicle.allowedActions.includes(AllowedAction.REPORT_UNANNOUNCED_DEPARTURE),
        });
        const details = [arrivalDetails, departureDetails];

        return (
            <Sidebar
                {...sidebarProperties}
                closed={false}
                onClose={closeSidebar}
                title={title}
                titleClassName="padding-left-10"
                footer={footerButton}
                footerClassName="margin-right-10"
            >
                <CompoundVehicleVIN vehicle={selectedVehicle.vehicle} />
                <CompoundLocationAndDestinationDetails
                    compoundLocation={selectedVehicle.compoundLocation}
                    vehicle={selectedVehicle.vehicle}
                />
                <ExpanderList items={details} className="margin-20" unmountOnExit={false} />
                <CustomsInformationExpander customsInformation={selectedVehicle.vehicle.customsInformation} />
                <VehicleDetails vehicle={selectedVehicle.vehicle} isCancelled={false} />
            </Sidebar>
        );
    } else {
        return <Sidebar {...sidebarProperties} closed />;
    }
};

const buildArrivalDetailsItem = (props: ArrivalDetailsProps): ExpanderListItem => {
    const isCancelled = props.arrival.isCancelled;

    return {
        id: 'arrival',
        open: true,
        header: isCancelled ? <CancelledArrivalDetailsHeader isWarning /> : <ArrivalDetailsHeader />,
        body: <ArrivalDetails {...props} />,
        bodyClassName: 'border-top-only border-width-1 border-style-solid border-color-light',
        className: isCancelled ? 'list-group-item-warning' : undefined,
    };
};

const buildNoArrivalDetailsItem = (): ExpanderListItem => ({
    id: 'arrival',
    open: false,
    header: <NoArrivalDetailsHeader />,
});

const buildDepartureDetailsItem = (props: DepartureDetailsProps): ExpanderListItem => {
    const isCancelled = props.departure.isCancelled;

    return {
        id: 'departure',
        open: true,
        header: isCancelled ? <CancelledDepartureDetailsHeader /> : <DepartureDetailsHeader />,
        body: <DepartureDetails {...props} />,
        bodyClassName: 'border-top-only border-width-1 border-style-solid border-color-light',
        className: isCancelled ? 'list-group-item-danger' : undefined,
    };
};
